import KitButton from '@omni/kit/components/KitButton';
import KitContainedIcon from '@omni/kit/components/KitContainedIcon';
import KitLink from '@omni/kit/components/KitLink';
import KitSnack from '@omni/kit/components/KitSnack';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import KitSnackRender from '@omni/kit/components/KitSnackRender';
import KitText from '@omni/kit/components/KitText';
import KitVerifyCodeInput from '@omni/kit/components/KitVerifyCodeInput';
import Show from '@omni/kit/components/Show';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClassV2, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import sanitizePhoneNumber from '@omni/kit/utilities/sanitizePhoneNumber';
import React, { ReactElement, useMemo, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { IS_LOCALHOST } from '../Environment';
import HealthMonitor from '../HealthMonitor';
import {
  SUBSPLASH_AUTH_PROVIDER_ID,
  SUBSPLASH_PHONE_AUTH_PROVIDER_ID,
} from '../constants/identifiers';
import { useCaptcha } from '../contexts/GoogleCaptchaContext';
import useShellAuthProviders from '../contexts/ShellContext/hooks/useShellAuthProviders';
import { useCountdownTimer } from '../hooks/useCountdownTimer';
import { handleErrorResponse } from '../utilities/utilities';
import AuthFooter from './AuthFooter';
import PhoneLoginDisabledScreen from './PhoneLoginDisabledScreen';
import { AuthService } from './Service';
import { AuthProvider } from './Service/Types';
import TooManyAttemptsScreen from './TooManyAttemptsScreen';

interface VerifyCodeScreenProps {
  appHeaders: Record<string, string>;
  authUrl: string;
  callingCode: string;
  connectAuthenticatorUrl: string;
  confirmEmailOtpUrl: string;
  confirmPhoneOtpUrl: string;
  email?: string;
  emailOtpAuthState?: string;
  firstNameValue?: string;
  identityAccessToken?: string;
  identityRefreshToken?: string;
  lastNameValue?: string;
  phoneAuthenticatorId?: string;
  phoneNumber: string;
  phoneOtpAuthState: string;
  regionCode: string;
  reset: () => void;
  setAuthenticatorNotFoundScreenVisible: (visible: boolean) => void;
  setConnectAuthenticatorUrl: (url: string) => void;
  setIdentityAccessToken: (accessToken: string) => void;
  setIdentityRefreshToken: (refreshToken: string) => void;
  setPhoneAuthenticatorId: (authenticatorId: string) => void;
  setVerifyCodeScreenVisible: (visible: boolean) => void;
  showFooter: boolean;
  suppressSubsplashBranding: boolean;
}

const VerifyCodeScreen = ({
  appHeaders,
  authUrl,
  callingCode,
  confirmEmailOtpUrl,
  confirmPhoneOtpUrl,
  connectAuthenticatorUrl,
  email,
  emailOtpAuthState,
  firstNameValue,
  identityAccessToken,
  identityRefreshToken,
  lastNameValue,
  phoneAuthenticatorId,
  phoneNumber,
  phoneOtpAuthState,
  regionCode,
  reset,
  setAuthenticatorNotFoundScreenVisible,
  setConnectAuthenticatorUrl,
  setIdentityAccessToken,
  setIdentityRefreshToken,
  setPhoneAuthenticatorId,
  setVerifyCodeScreenVisible,
  showFooter,
  suppressSubsplashBranding,
}: VerifyCodeScreenProps): ReactElement => {
  // ------------------------------------------------------------
  // Context & Custom Hooks

  const {
    counter,
    isActive: isResendCodeDisabled,
    start: startCountdown,
    stop: stopCountdown,
  } = useCountdownTimer();
  const { loginEnabledProviders } = useShellAuthProviders();
  const { fetchCaptcha, siteKey } = useCaptcha();
  const { app, tokens, dispatchEvent } = useShellContext();
  const { sizeClassV2 } = useSizeClass();
  const { t } = useTranslation(['auth']);

  // ------------------------------------------------------------
  // State

  const [verificationCode, setVerificationCode] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
    marginBottom: Spacing.xxl,
  });
  const [phoneLoginDisabledScreenVisible, setPhoneLoginDisabledScreenVisible] =
    useState(false);
  const [tooManyAttemptsScreenVisible, setTooManyAttemptsScreenVisible] =
    useState(false);
  const [retryAfter, setRetryAfter] = useState<string | null>(null);

  // ------------------------------------------------------------
  // Computed Values

  const isMobileDevice = sizeClassV2 < SizeClassV2.M;

  const isEmailVerification = useMemo((): boolean => {
    return Boolean(email && firstNameValue && lastNameValue);
  }, [email, firstNameValue, lastNameValue]);

  // ------------------------------------------------------------
  // Event Handlers & Callbacks

  const confirmVerificationCode = async (): Promise<void> => {
    setIsVerifying(true);

    let response: Response;

    if (isEmailVerification) {
      response = await fetch(confirmEmailOtpUrl, {
        method: 'POST',
        body: JSON.stringify({
          email,
          code: verificationCode,
          first_name: firstNameValue,
          last_name: lastNameValue,
          state: emailOtpAuthState,
          verification_site_key: siteKey,
          verification_token: await fetchCaptcha('confirm_email_otp'),
        }),
        // credentials: 'include' is required in order for the backend EUA
        // result handler to set the sap_token cookie
        ...(Platform.OS === 'web' &&
          !IS_LOCALHOST && { credentials: 'include' }),
      });
    } else {
      response = await fetch(confirmPhoneOtpUrl, {
        method: 'POST',
        body: JSON.stringify({
          code: verificationCode,
          grant_type: 'otp',
          phone: `${callingCode}${sanitizePhoneNumber(phoneNumber)}`,
          state: phoneOtpAuthState,
          verification_site_key: siteKey,
          verification_token: await fetchCaptcha('confirm_phone_otp'),
        }),
        // credentials: 'include' is required in order for the backend EUA
        // result handler to set the sap_token cookie
        ...(Platform.OS === 'web' &&
          !IS_LOCALHOST && { credentials: 'include' }),
        headers: {
          ...appHeaders,
          Authorization: `Bearer ${tokens.guest}`,
          'Content-Type': 'application/json',
        },
      });
    }

    if (response.ok) {
      if (response.url) {
        const resultUrl = response.url;
        const urlParams = new URL(resultUrl).searchParams;
        const accessToken = urlParams.get('access_token');
        const sapToken = urlParams.get('sap_token');
        const subsplashProvider = loginEnabledProviders.find(
          (provider) => provider.authproviderid === SUBSPLASH_AUTH_PROVIDER_ID
        ) as AuthProvider;

        if (accessToken && sapToken) {
          if (isEmailVerification) {
            const response = await fetch(connectAuthenticatorUrl, {
              method: 'POST',
              body: JSON.stringify({
                access_token: identityAccessToken,
                refresh_token: identityRefreshToken,
                authprovider_id: SUBSPLASH_PHONE_AUTH_PROVIDER_ID,
              }),
              headers: {
                ...appHeaders,
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            });

            // We don't want to block the user from logging in if this fails,
            // just log the error to Sentry
            if (!response.ok) {
              const { data, status } = await handleErrorResponse(response);

              if (status >= 400 && status !== 429) {
                HealthMonitor.logError(data, {
                  tags: {
                    label:
                      'auth:failed to connect phone authenticator to end user',
                  },
                });
              }
            }
          }

          AuthService.setAccessToken(SUBSPLASH_AUTH_PROVIDER_ID, accessToken);
          AuthService.refreshToken = sapToken;
          AuthService.setRefreshTokenEndpoint(
            SUBSPLASH_AUTH_PROVIDER_ID,
            subsplashProvider.token_url
          );
          AuthService.sendTokenDataToNative({
            authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
            accessToken,
            refreshToken: sapToken,
          });

          await AuthService.loadCredentials({
            appKey: app.appKey,
            targetProviders: [subsplashProvider],
            sapToken,
          });

          AuthService.notifyAuthorizationChanged();

          if (Platform.OS === 'web') {
            // update login state in the web-app
            dispatchEvent({
              type: 'AUTH_CALLBACK',
              payload: {
                refresh: sapToken,
              },
            });
          }
        }
      }
    } else {
      const responseData = await response.json();

      if (
        response.status === 401 &&
        responseData.error.detail.includes('invalid code')
      ) {
        if (responseData.remaining_attempts) {
          setErrorMessage(
            t('auth:invalidCodeWithRemainingAttempts', {
              remainingAttempts: responseData.remaining_attempts,
              attempts:
                responseData.remaining_attempts === 1
                  ? t('auth:attempt')
                  : t('auth:attempts'),
            })
          );
        } else {
          setErrorMessage(t('auth:invalidCode'));
        }

        setShowError(true);
      } else if (
        response.status === 429 &&
        responseData.error.detail.includes('request temporarily blocked')
      ) {
        setRetryAfter(response.headers.get('retry-after'));
        setTooManyAttemptsScreenVisible(true);
      } else if (
        !isEmailVerification &&
        response.status === 423 &&
        (responseData.error.detail.includes('too many failed attempts') ||
          responseData.error.detail.includes(
            'account locked due to too many failed attempts'
          ))
      ) {
        setPhoneLoginDisabledScreenVisible(true);
      } else if (
        !isEmailVerification &&
        response.status === 404 &&
        responseData.error?.code?.includes('resource_not_found')
      ) {
        setVerifyCodeScreenVisible(false);
        setAuthenticatorNotFoundScreenVisible(true);
        setConnectAuthenticatorUrl(responseData.connect_authenticator_url);
        setIdentityAccessToken(responseData.identity_tokens.access_token);
        setIdentityRefreshToken(responseData.identity_tokens.refresh_token);
        setPhoneAuthenticatorId(
          responseData.identity_tokens.phone_authenticator_id
        );
      } else {
        KitSnack.show(t('common:oopsSomethingWentWrongPleaseTryAgainLater'));
      }
    }

    setIsVerifying(false);
  };

  const resendCode = async (): Promise<void> => {
    setVerificationCode('');

    let response: Response;

    if (isEmailVerification) {
      const emailOtpUrl = loginEnabledProviders.find(
        (provider) => provider.authproviderid === SUBSPLASH_AUTH_PROVIDER_ID
      )?.otp_url;

      if (!emailOtpUrl) {
        return;
      }

      response = await fetch(emailOtpUrl, {
        method: 'POST',
        body: JSON.stringify({
          branding_app_key: app.appKey,
          email,
          phone_authenticator_id: phoneAuthenticatorId,
          verification_site_key: siteKey,
          verification_token: await fetchCaptcha('authorize_email_otp'),
          ...(Platform.OS === 'web' && {
            skip_deep_link: true,
            use_refresh_cookie: !IS_LOCALHOST,
          }),
        }),
      });
    } else {
      response = await fetch(authUrl, {
        method: 'POST',
        body: JSON.stringify({
          phone_number: {
            significant: sanitizePhoneNumber(phoneNumber),
            country: {
              region_code: regionCode,
              calling_code: callingCode,
            },
          },
          verification_site_key: siteKey,
          verification_token: await fetchCaptcha('authorize_phone'),
          ...(Platform.OS === 'web' && {
            skip_deep_link: true,
            use_refresh_cookie: !IS_LOCALHOST,
          }),
        }),
        headers: {
          ...appHeaders,
          Authorization: `Bearer ${tokens.guest}`,
          'Content-Type': 'application/json',
        },
      });
    }

    if (response.ok) {
      startCountdown();
    } else {
      const { data, message, status } = await handleErrorResponse(response);

      if (isMobileDevice) {
        showKitSnack(
          message ?? t('auth:newCodeSendFailed'),
          KitSnackDuration.SHORT
        );
      } else {
        KitSnack.show(message ?? t('auth:newCodeSendFailed'));
      }

      if (status >= 400 && status !== 429) {
        HealthMonitor.logError(data, {
          tags: {
            label: 'auth:failed to resend code',
          },
        });
      }
    }
  };

  const showKitSnack = (message: string, duration: number): void => {
    setSnackOptions({
      duration,
      marginBottom: Spacing.xxl,
      message,
      visible: true,
    });
  };

  const resetVerifyCodeScreen = () => {
    stopCountdown();
    reset();
  };

  // ------------------------------------------------------------
  // Render Helpers

  if (phoneLoginDisabledScreenVisible) {
    return <PhoneLoginDisabledScreen reset={resetVerifyCodeScreen} />;
  }

  if (tooManyAttemptsScreenVisible) {
    return (
      <TooManyAttemptsScreen
        reset={resetVerifyCodeScreen}
        retryAfter={retryAfter}
        isEmailVerification={isEmailVerification}
      />
    );
  }

  return (
    <View
      style={[styles.container, !isMobileDevice && { marginHorizontal: 100 }]}
    >
      <View style={styles.iconContainer}>
        <KitContainedIcon name='message' size='md' />
      </View>
      <View>
        <KitText center h1 style={styles.title} color={Colors.N900}>
          {t('auth:verifyCodePromptTitle', {
            verificationMethod: isEmailVerification
              ? t('auth:verificationMethodEmail')
              : t('auth:verificationMethodPhone'),
          })}
        </KitText>
        <KitText center style={styles.subtitle} color={Colors.N900}>
          {t('auth:verifyCodePromptSubtitle', {
            verificationMethodValue: isEmailVerification
              ? email
              : `${callingCode} ${phoneNumber}`,
          })}
        </KitText>
      </View>
      <KitVerifyCodeInput
        setValue={(code) => {
          setVerificationCode(code);
          setShowError(false);
        }}
        value={verificationCode}
        returnKeyType='go'
        blurOnSubmit
        numeric
        onSubmitEditing={() => {
          if (!isVerifying && verificationCode.length === 6) {
            confirmVerificationCode();
          }
        }}
        onAutoSubmit={confirmVerificationCode}
      >
        <View
          style={{
            gap: Spacing.m,
            marginTop: Spacing.xl,
          }}
        >
          <Show show={showError}>
            <View style={{ ...styles.errorContainer, marginBottom: 6 }}>
              <KitText color={Colors.N0} fontSize={16}>
                {errorMessage}
              </KitText>
            </View>
          </Show>
          <KitButton
            color={Colors.N1000}
            disabled={isVerifying || verificationCode.length !== 6 || showError}
            onPress={confirmVerificationCode}
            isLoading={isVerifying}
            title={t('auth:verifyCode')}
          />
          <KitButton
            secondary
            onPress={resendCode}
            title={
              isResendCodeDisabled
                ? t('auth:requestNewCodeIn', {
                    seconds: counter,
                  })
                : t('auth:iDidntGetCode')
            }
            disabled={isResendCodeDisabled || isVerifying}
          />
        </View>
      </KitVerifyCodeInput>
      <KitLink
        fontSize={16}
        style={styles.link}
        onPress={resetVerifyCodeScreen}
      >
        {t('auth:logInAnotherWay')}
      </KitLink>
      <AuthFooter
        showFooter={showFooter}
        suppressSubsplashBranding={suppressSubsplashBranding}
        showRecaptcha={true}
      />
      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    justifyContent: 'center',
    paddingHorizontal: Spacing.xl,
  },
  errorContainer: {
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.m,
    backgroundColor: Colors.R500,
    borderRadius: BorderRadius.m,
    marginBottom: Spacing.l,
    width: '100%',
  },
  iconContainer: {
    marginBottom: Spacing.l,
    alignItems: 'center',
  },
  link: {
    color: Colors.N900,
    marginTop: Spacing.xl,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: Spacing.xl,
  },
  text: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
  },
  title: {
    textAlign: 'center',
    marginBottom: Spacing.s,
  },
});

export default VerifyCodeScreen;
